import React, { useState, useEffect } from 'react';
import contentService from '../services/contentService';
import './MercadoPage.css';

import Loading from '../components/Loading';
function fmtFollowers(n) {
  if (!n && n !== 0) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

// ── Avatar ────────────────────────────────────────────────────────────
function ProfileAvatar({ profile, size = 44 }) {
  const [err, setErr] = useState(false);
  const initials = (profile.username || '').substring(0, 2).toUpperCase();
  if (profile.profile_pic_url && !err) {
    return (
      <img
        src={profile.profile_pic_url}
        alt={profile.username}
        onError={() => setErr(true)}
        style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
      />
    );
  }
  return (
    <div className="mkt-avatar-ph" style={{ width: size, height: size, fontSize: size * 0.35 }}>
      {initials}
    </div>
  );
}

// ── Helpers visuales ─────────────────────────────────────────────────
const MEDIA_LABELS = { IMAGE: 'Posts', VIDEO: 'Videos', CAROUSEL_ALBUM: 'Carruseles', REEL: 'Reels' };
const MEDIA_COLORS = { IMAGE: '#6366f1', VIDEO: '#f59e0b', CAROUSEL_ALBUM: '#10b981', REEL: '#e1306c' };
const SAFETY_COLOR = { safe: '#10b981', caution: '#f59e0b', risky: '#ef4444' };
const DAY_ORDER    = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

function ContentBar({ label, count, total, color }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div style={{ marginBottom: 6 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 3 }}>
        <span style={{ color: '#374151', fontWeight: 600 }}>{label}</span>
        <span style={{ color: '#6b7280' }}>{count} ({pct}%)</span>
      </div>
      <div style={{ height: 6, background: '#f3f4f6', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 4, transition: 'width 0.4s' }} />
      </div>
    </div>
  );
}

// ── Tarjeta de perfil expandible (competencia / referencia) ───────────
function ProfileCard({ profile, onRemove, role, marcaId }) {
  const [expanded,  setExpanded]  = useState(false);
  const [stats,     setStats]     = useState(null);
  const [analysis,  setAnalysis]  = useState(null);
  const [loadStats, setLoadStats] = useState(false);
  const [loadAI,    setLoadAI]    = useState(false);
  const [errStats,  setErrStats]  = useState(null);
  const [errAI,     setErrAI]     = useState(null);
  const [analysisDate, setAnalysisDate] = useState(null);
  const [tiktokUser, setTiktokUser] = useState(profile.tiktok_username || '');
  const [savingTT,   setSavingTT]   = useState(false);
  const [editingTT,  setEditingTT]  = useState(false);

  const handleTikTokSave = async () => {
    setSavingTT(true);
    try {
      await contentService.setProfileTikTok(profile.id, tiktokUser);
      setEditingTT(false);
    } finally {
      setSavingTT(false);
    }
  };

  const handleExpand = async () => {
    const next = !expanded;
    setExpanded(next);
    if (next && !stats) {
      setLoadStats(true);
      setErrStats(null);
      try {
        const r = await contentService.getMercadoProfileStats(profile.id);
        setStats(r.data);
        // Si hay análisis cacheado para la misma marca, usarlo directamente
        if (r.data.mercado_compare_result && r.data.mercado_compare_marca_id === marcaId) {
          setAnalysis(r.data.mercado_compare_result);
          setAnalysisDate(r.data.mercado_compare_at);
        }
      } catch (e) {
        setErrStats('No se pudieron cargar las estadísticas.');
      } finally {
        setLoadStats(false);
      }
    }
  };

  const handleAnalysis = async (force = false) => {
    if (loadAI) return;
    if (!marcaId) { setErrAI('Selecciona primero "Mi Marca" para comparar.'); return; }
    setLoadAI(true);
    setErrAI(null);
    try {
      const r = await contentService.getMercadoCompare(profile.id, marcaId, force);
      setAnalysis(r.data.analysis);
      setAnalysisDate(r.data.cached_at);
    } catch (e) {
      setErrAI('Error al generar el análisis con Claude.');
    } finally {
      setLoadAI(false);
    }
  };

  // ordenar días según semana laboral
  const sortedDays = stats?.posting_days
    ? [...stats.posting_days].sort((a, b) => DAY_ORDER.indexOf(a.day) - DAY_ORDER.indexOf(b.day))
    : [];
  const maxDay = sortedDays.length ? Math.max(...sortedDays.map(d => d.count)) : 1;
  const totalPosts = stats?.content_distribution?.reduce((s, c) => s + c.count, 0) || 0;

  return (
    <div className={`mkt-profile-card mkt-profile-card--${role}`} style={{ flexDirection: 'column', alignItems: 'stretch', gap: 0 }}>
      {/* Cabecera */}
      <div className="mkt-card-row">
        <ProfileAvatar profile={profile} size={46} />
        <div className="mkt-profile-card-info" style={{ flex: 1 }}>
          <span className="mkt-profile-card-username">@{profile.username}</span>
          {profile.full_name && <span className="mkt-profile-card-name">{profile.full_name}</span>}
          <div className="mkt-profile-card-meta">
            <span>{fmtFollowers(profile.followers_count)} seg.</span>
            {profile.ai_category && <span className="mkt-profile-card-cat">{profile.ai_category}</span>}
          </div>
        </div>
        <div className="mkt-card-controls">
          {/* TikTok handle */}
          {editingTT ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600 }}>TT</span>
              <input
                autoFocus
                value={tiktokUser}
                onChange={e => setTiktokUser(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleTikTokSave(); if (e.key === 'Escape') setEditingTT(false); }}
                placeholder="@handle"
                disabled={savingTT}
                style={{
                  fontSize: 11, padding: '3px 6px', width: 90,
                  border: '1px solid #818cf8', borderRadius: 6, outline: 'none',
                }}
              />
              <button
                onClick={handleTikTokSave}
                disabled={savingTT}
                style={{ fontSize: 11, padding: '3px 7px', borderRadius: 6, border: '1px solid #818cf8', background: '#818cf8', color: '#fff', cursor: 'pointer', fontWeight: 600 }}
              >{savingTT ? '…' : '✓'}</button>
              <button
                onClick={() => setEditingTT(false)}
                style={{ fontSize: 11, padding: '3px 7px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer' }}
              >✕</button>
            </div>
          ) : (
            <button
              onClick={() => setEditingTT(true)}
              title={tiktokUser ? `TikTok: @${tiktokUser}` : 'Agregar TikTok'}
              style={{
                fontSize: 11, padding: '3px 8px', borderRadius: 6,
                border: '1px solid #d1d5db',
                background: tiktokUser ? '#f0f0fe' : '#fff',
                color: tiktokUser ? '#4f46e5' : '#9ca3af',
                cursor: 'pointer', fontWeight: tiktokUser ? 600 : 400,
                whiteSpace: 'nowrap',
              }}
            >
              {tiktokUser ? `TT @${tiktokUser}` : '+ TikTok'}
            </button>
          )}
          <button
            onClick={handleExpand}
            title={expanded ? 'Contraer' : 'Ver análisis'}
            style={{
              background: expanded ? '#f0fdf4' : '#fff',
              border: '1px solid #d1d5db',
              borderRadius: 7, padding: '4px 10px',
              fontSize: 12, fontWeight: 600, cursor: 'pointer',
              color: expanded ? '#1d6c38' : '#374151',
            }}
          >
            {expanded ? '▲ Menos' : '▼ Más'}
          </button>
          <button className="mkt-remove-btn" onClick={onRemove} title="Quitar">×</button>
        </div>
      </div>

      {/* Panel expandible */}
      {expanded && (
        <div style={{ marginTop: 14, borderTop: '1px solid #f3f4f6', paddingTop: 14 }}>
          {loadStats && (
            <Loading fullScreen={false} mensaje="Cargando estadísticas" />
          )}
          {errStats && <p style={{ color: '#ef4444', fontSize: 13 }}>{errStats}</p>}

          {stats && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>

              {/* 1. Colaboradores */}
              <div style={{ gridColumn: stats.collaborators.length === 0 ? 'span 1' : undefined }}>
                <p style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Colaboradores frecuentes
                </p>
                {stats.collaborators.length === 0 ? (
                  <p style={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>Sin colaboraciones registradas</p>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {stats.collaborators.map(c => (
                      <a
                        key={c.username}
                        href={`https://instagram.com/${c.username}`}
                        target="_blank" rel="noopener noreferrer"
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          background: '#f3f4f6', borderRadius: 20,
                          padding: '3px 10px', fontSize: 12, fontWeight: 600,
                          color: '#374151', textDecoration: 'none',
                        }}
                      >
                        @{c.username}
                        <span style={{ color: '#9ca3af', fontWeight: 400 }}>×{c.count}</span>
                      </a>
                    ))}
                  </div>
                )}
              </div>

              {/* 2. Tipos de contenido */}
              <div>
                <p style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Tipo de contenido ({totalPosts} posts)
                </p>
                {stats.content_distribution.length === 0 ? (
                  <p style={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>Sin datos</p>
                ) : (
                  stats.content_distribution.map(c => (
                    <ContentBar
                      key={c.media_type}
                      label={MEDIA_LABELS[c.media_type] || c.media_type}
                      count={c.count}
                      total={totalPosts}
                      color={MEDIA_COLORS[c.media_type] || '#6b7280'}
                    />
                  ))
                )}
              </div>

              {/* 3. Días de publicación */}
              <div style={{ gridColumn: '1 / -1' }}>
                <p style={{ margin: '0 0 10px', fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Días de publicación más frecuentes
                </p>
                {sortedDays.length === 0 ? (
                  <p style={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>Sin datos de fechas</p>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 60, marginTop: 28 }}>
                    {DAY_ORDER.map(dayName => {
                      const d = sortedDays.find(x => x.day === dayName);
                      const cnt = d?.count || 0;
                      const h = maxDay > 0 ? Math.round((cnt / maxDay) * 52) : 0;
                      const isTop = cnt === maxDay && cnt > 0;
                      return (
                        <div key={dayName} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                          <span style={{ fontSize: 10, color: isTop ? '#1d6c38' : '#9ca3af', fontWeight: isTop ? 700 : 400 }}>
                            {cnt || ''}
                          </span>
                          <div
                            title={`${dayName}: ${cnt} posts`}
                            style={{
                              width: '100%', height: h || 3,
                              background: isTop ? '#1d6c38' : cnt > 0 ? '#bbf7d0' : '#f3f4f6',
                              borderRadius: '3px 3px 0 0', transition: 'height 0.3s',
                            }}
                          />
                          <span style={{ fontSize: 10, color: '#6b7280' }}>{dayName}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* 4. Rendimiento por colaborador y tipo de contenido */}
              {(stats.collaborators.length > 0 || stats.content_performance?.length > 0) && (
                <div style={{ gridColumn: '1 / -1', borderTop: '1px solid #f3f4f6', paddingTop: 14 }}>
                  <p style={{ margin: '0 0 10px', fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    ¿Qué funciona mejor?
                  </p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>

                    {/* Top colaboradores por engagement */}
                    {stats.collaborators.length > 0 && (
                      <div>
                        <p style={{ margin: '0 0 8px', fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase' }}>
                          Top colaboradores (por engagement)
                        </p>
                        {stats.collaborators.slice(0, 3).map((c, i) => {
                          const medals = ['1°', '2°', '3°'];
                          return (
                            <div key={c.username} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', background: i === 0 ? '#fefce8' : '#fafafa', borderRadius: 7, marginBottom: 5, border: i === 0 ? '1px solid #fde68a' : '1px solid #f3f4f6' }}>
                              <span style={{ fontSize: 12, fontWeight: 700, color: '#374151' }}>
                                {medals[i]} @{c.username}
                              </span>
                              <div style={{ textAlign: 'right', fontSize: 11, color: '#6b7280' }}>
                                <div>❤️ {c.avg_likes.toLocaleString('es-CL')} · 💬 {c.avg_comments.toLocaleString('es-CL')}</div>
                                <div style={{ color: '#9ca3af' }}>{c.count} collab{c.count !== 1 ? 's' : ''}</div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Mejor tipo de contenido */}
                    {stats.content_performance?.length > 0 && (
                      <div>
                        <p style={{ margin: '0 0 8px', fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase' }}>
                          Mejor tipo de contenido (por engagement)
                        </p>
                        {stats.content_performance.slice(0, 3).map((cp, i) => {
                          const medals = ['1°', '2°', '3°'];
                          const label = MEDIA_LABELS[cp.media_type] || cp.media_type;
                          const color = MEDIA_COLORS[cp.media_type] || '#6b7280';
                          return (
                            <div key={cp.media_type} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', background: i === 0 ? '#f0fdf4' : '#fafafa', borderRadius: 7, marginBottom: 5, border: i === 0 ? `1px solid ${color}44` : '1px solid #f3f4f6' }}>
                              <span style={{ fontSize: 12, fontWeight: 700, color }}>
                                {medals[i]} {label}
                              </span>
                              <div style={{ textAlign: 'right', fontSize: 11, color: '#6b7280' }}>
                                <div>❤️ {cp.avg_likes.toLocaleString('es-CL')} · 💬 {cp.avg_comments.toLocaleString('es-CL')}</div>
                                <div style={{ color: '#9ca3af' }}>{cp.count} post{cp.count !== 1 ? 's' : ''}</div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                  </div>
                </div>
              )}

              {/* 5. Fórmula óptima: cruce día × tipo × collab */}
              {stats.formula_ganadora?.length > 0 && (
                <div style={{ gridColumn: '1 / -1', borderTop: '1px solid #f3f4f6', paddingTop: 14 }}>
                  <p style={{ margin: '0 0 12px', fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Fórmula óptima de publicación
                  </p>

                  {/* Bloque 1: Top 3 fórmulas ganadoras */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 16 }}>
                    {stats.formula_ganadora.map((f, i) => {
                      const medals  = ['1°', '2°', '3°'];
                      const bgs     = ['#fefce8', '#f8fafc', '#fafaf9'];
                      const borders = ['#fde68a', '#e2e8f0', '#e7e5e4'];
                      const label   = MEDIA_LABELS[f.media_type] || f.media_type;
                      const color   = MEDIA_COLORS[f.media_type] || '#6b7280';
                      return (
                        <div key={i} style={{ background: bgs[i], border: `1px solid ${borders[i]}`, borderRadius: 10, padding: '10px 12px' }}>
                          <div style={{ fontSize: 16, marginBottom: 4 }}>{medals[i]}</div>
                          <div style={{ fontSize: 13, fontWeight: 700, color, marginBottom: 6 }}>{label}</div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <span style={{ fontSize: 11, background: '#f3f4f6', borderRadius: 12, padding: '2px 8px', display: 'inline-block', color: '#374151', fontWeight: 600 }}>
                              📅 {f.day}
                            </span>
                            <span style={{ fontSize: 11, background: f.is_collab ? '#ecfdf5' : '#f3f4f6', borderRadius: 12, padding: '2px 8px', display: 'inline-block', color: f.is_collab ? '#059669' : '#6b7280', fontWeight: 600 }}>
                              {f.is_collab ? '🤝 Con collab' : '👤 Solo'}
                            </span>
                          </div>
                          <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #f3f4f6' }}>
                            <div style={{ fontSize: 12, fontWeight: 800, color: '#111827' }}>
                              {f.avg_engagement.toLocaleString('es-CL')} eng.
                            </div>
                            <div style={{ fontSize: 10, color: '#9ca3af' }}>
                              ❤️ {f.avg_likes.toLocaleString('es-CL')} · 💬 {f.avg_comments.toLocaleString('es-CL')} · {f.n} posts
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Bloque 2: Tabla por tipo de contenido */}
                  {stats.por_tipo && Object.keys(stats.por_tipo).length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                      <p style={{ margin: '0 0 8px', fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase' }}>
                        Mejor estrategia por tipo
                      </p>
                      <div style={{ borderRadius: 8, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                          <thead>
                            <tr style={{ background: '#f9fafb' }}>
                              <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 700, color: '#374151' }}>Tipo</th>
                              <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 700, color: '#374151' }}>Mejor día</th>
                              <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 700, color: '#374151' }}>¿Con collab?</th>
                              <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 700, color: '#374151' }}>Impacto collab</th>
                              <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700, color: '#374151' }}>Mejor eng.</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(stats.por_tipo).map(([mt, d], idx) => {
                              const label = MEDIA_LABELS[mt] || mt;
                              const color = MEDIA_COLORS[mt] || '#6b7280';
                              const boost = d.collab_boost_pct;
                              const boostColor = boost == null ? '#9ca3af' : boost > 0 ? '#10b981' : '#ef4444';
                              const boostText  = boost == null ? '—' : boost > 0 ? `+${boost}%` : `${boost}%`;
                              return (
                                <tr key={mt} style={{ borderTop: idx > 0 ? '1px solid #f3f4f6' : 'none', background: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                                  <td style={{ padding: '8px 12px' }}>
                                    <span style={{ fontWeight: 700, color }}>{label}</span>
                                  </td>
                                  <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                                    <span style={{ background: '#eff6ff', color: '#2563eb', borderRadius: 12, padding: '2px 8px', fontWeight: 600 }}>
                                      {d.mejor_dia || '—'}
                                    </span>
                                  </td>
                                  <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                                    {d.collab_boost_pct == null ? '—' : d.collab_boost_pct > 0
                                      ? <span style={{ color: '#059669', fontWeight: 700 }}>Sí</span>
                                      : <span style={{ color: '#6b7280', fontWeight: 600 }}>No</span>}
                                  </td>
                                  <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                                    <span style={{ fontWeight: 700, color: boostColor }}>{boostText}</span>
                                    {boost != null && (
                                      <div style={{ fontSize: 10, color: '#9ca3af' }}>
                                        {d.n_con_collab} vs {d.n_sin_collab} posts
                                      </div>
                                    )}
                                  </td>
                                  <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700, color: '#111827' }}>
                                    {d.mejor_combo_engagement?.toLocaleString('es-CL') ?? '—'}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Bloque 3: Impacto del collab — barras comparativas */}
                  {stats.collab_impact?.length > 0 && (
                    <div>
                      <p style={{ margin: '0 0 8px', fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase' }}>
                        Collab vs Solo — comparación de engagement
                      </p>
                      {stats.collab_impact.filter(c => c.con_collab != null && c.sin_collab != null).map(c => {
                        const label   = MEDIA_LABELS[c.media_type] || c.media_type;
                        const color   = MEDIA_COLORS[c.media_type] || '#6b7280';
                        const maxVal  = Math.max(c.con_collab || 0, c.sin_collab || 0) || 1;
                        const pctCon  = Math.round((c.con_collab / maxVal) * 100);
                        const pctSin  = Math.round((c.sin_collab / maxVal) * 100);
                        const boost   = c.boost_pct;
                        const boostColor = boost > 0 ? '#10b981' : '#ef4444';
                        return (
                          <div key={c.media_type} style={{ marginBottom: 10 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                              <span style={{ fontSize: 12, fontWeight: 700, color }}>{label}</span>
                              {boost != null && (
                                <span style={{ fontSize: 11, fontWeight: 700, color: boostColor }}>
                                  {boost > 0 ? `Collab suma ${boost}%` : `Collab resta ${Math.abs(boost)}%`}
                                </span>
                              )}
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                              <div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#6b7280', marginBottom: 2 }}>
                                  <span>Con collab ({c.n_collab} posts)</span>
                                  <span style={{ fontWeight: 600 }}>{(c.con_collab || 0).toLocaleString('es-CL')}</span>
                                </div>
                                <div style={{ height: 8, background: '#f3f4f6', borderRadius: 4, overflow: 'hidden' }}>
                                  <div style={{ height: '100%', width: `${pctCon}%`, background: '#059669', borderRadius: 4 }} />
                                </div>
                              </div>
                              <div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#6b7280', marginBottom: 2 }}>
                                  <span>Sin collab ({c.n_solo} posts)</span>
                                  <span style={{ fontWeight: 600 }}>{(c.sin_collab || 0).toLocaleString('es-CL')}</span>
                                </div>
                                <div style={{ height: 8, background: '#f3f4f6', borderRadius: 4, overflow: 'hidden' }}>
                                  <div style={{ height: '100%', width: `${pctSin}%`, background: '#94a3b8', borderRadius: 4 }} />
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                </div>
              )}

              {/* 7. Análisis Claude */}
              <div style={{ gridColumn: '1 / -1', borderTop: '1px solid #f3f4f6', paddingTop: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <div>
                    <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Análisis vs Mi Marca
                    </p>
                    {analysisDate && (
                      <p style={{ margin: '2px 0 0', fontSize: 10, color: '#9ca3af' }}>
                        Analizado el {new Date(analysisDate).toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </p>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {analysis && (
                      <button
                        onClick={() => handleAnalysis(true)}
                        disabled={loadAI}
                        title="Generar nuevo análisis"
                        style={{
                          background: '#f3f4f6', color: '#6b7280',
                          border: '1px solid #e5e7eb', borderRadius: 7,
                          padding: '5px 12px', fontSize: 11, fontWeight: 600,
                          cursor: loadAI ? 'default' : 'pointer',
                        }}
                      >
                        {loadAI ? '…' : '↺ Re-analizar'}
                      </button>
                    )}
                    {!analysis && (
                      <button
                        onClick={() => handleAnalysis(false)}
                        disabled={loadAI}
                        style={{
                          background: loadAI ? '#f3f4f6' : 'linear-gradient(135deg,#0f4c23,#1d6c38)',
                          color: loadAI ? '#9ca3af' : '#fff',
                          border: 'none', borderRadius: 7,
                          padding: '5px 14px', fontSize: 12, fontWeight: 700,
                          cursor: loadAI ? 'default' : 'pointer',
                        }}
                      >
                        {loadAI ? 'Analizando…' : '🤖 Generar con Claude'}
                      </button>
                    )}
                  </div>
                </div>

                {errAI && <p style={{ color: '#ef4444', fontSize: 13 }}>{errAI}</p>}

                {!analysis && !loadAI && !errAI && (
                  <p style={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>
                    {marcaId ? 'Haz clic en "Generar con Claude" para comparar este perfil contra Tu Marca.' : 'Selecciona "Mi Marca" primero para habilitar la comparación.'}
                  </p>
                )}

                {analysis && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    {[
                      { key: 'fortalezas_competidor',  label: 'Fortalezas del competidor', color: '#ef4444', bg: '#fff5f5' },
                      { key: 'debilidades_competidor', label: 'Debilidades del competidor', color: '#10b981', bg: '#f0fdf4' },
                      { key: 'oportunidades_marca',    label: 'Oportunidades para tu marca', color: '#6366f1', bg: '#eef2ff' },
                      { key: 'amenazas_marca',         label: 'Amenazas para tu marca',     color: '#f59e0b', bg: '#fffbeb' },
                    ].map(({ key, label, color, bg }) => (
                      <div key={key} style={{ background: bg, borderRadius: 8, padding: '10px 12px' }}>
                        <p style={{ margin: '0 0 6px', fontSize: 11, fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                          {label}
                        </p>
                        <ul style={{ margin: 0, padding: '0 0 0 14px' }}>
                          {(analysis[key] || []).map((item, i) => (
                            <li key={i} style={{ fontSize: 12, color: '#374151', marginBottom: 3 }}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                    <div style={{ gridColumn: '1 / -1', background: '#f8fafc', borderRadius: 8, padding: '10px 12px', borderLeft: '3px solid #1d6c38' }}>
                      <p style={{ margin: '0 0 4px', fontSize: 11, fontWeight: 700, color: '#1d6c38', textTransform: 'uppercase' }}>Conclusión estratégica</p>
                      <p style={{ margin: 0, fontSize: 13, color: '#374151', lineHeight: 1.5 }}>{analysis.conclusion}</p>
                    </div>
                  </div>
                )}
              </div>

            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Fila en el modal ──────────────────────────────────────────────────
function PickerRow({ profile, onSelect, selected }) {
  return (
    <div
      className={`mkt-picker-row${selected ? ' mkt-picker-row--selected' : ''}`}
      onClick={() => onSelect(profile)}
    >
      <ProfileAvatar profile={profile} size={36} />
      <div className="mkt-picker-row-info">
        <span className="mkt-picker-row-user">@{profile.username}</span>
        {profile.full_name && <span className="mkt-picker-row-name">{profile.full_name}</span>}
      </div>
      <span className="mkt-picker-row-followers">{fmtFollowers(profile.followers_count)}</span>
      {selected && <span className="mkt-picker-row-check">✓</span>}
    </div>
  );
}

// ── Modal de selección ────────────────────────────────────────────────
function ProfilePickerModal({ onClose, onSelect, excludeIds = [], multi = false, selectedIds = [] }) {
  const [q, setQ]               = useState('');
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading]   = useState(false);
  const [creating, setCreating] = useState(false);
  const [picked, setPicked]     = useState(new Set(selectedIds));

  const handleCreateNew = async () => {
    const username = q.trim().replace('@', '').toLowerCase();
    if (!username) return;
    setCreating(true);
    try {
      const res = await contentService.createTrackedProfile(username);
      onSelect([res.data]);
      onClose();
    } finally {
      setCreating(false);
    }
  };

  useEffect(() => {
    const term = q.trim().replace('@', '');
    if (term.length < 2) { setProfiles([]); return; }
    setLoading(true);
    const timer = setTimeout(() => {
      contentService.getTrackedProfiles({ ordering: '-followers_count', q: term })
        .then(r => setProfiles(r.data.profiles || []))
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [q]);

  const visible = profiles.filter(p => !excludeIds.includes(p.id));

  const handleSelect = (profile) => {
    if (!multi) { onSelect([profile]); onClose(); return; }
    setPicked(prev => {
      const next = new Set(prev);
      next.has(profile.id) ? next.delete(profile.id) : next.add(profile.id);
      return next;
    });
  };

  const handleConfirm = () => {
    onSelect(profiles.filter(p => picked.has(p.id)));
    onClose();
  };

  return (
    <div className="mkt-modal-overlay" onClick={onClose}>
      <div className="mkt-modal" onClick={e => e.stopPropagation()}>
        <div className="mkt-modal-header">
          <h3>Seleccionar perfil de /tracked/</h3>
          <button className="mkt-modal-close" onClick={onClose}>×</button>
        </div>
        <input
          className="mkt-modal-search"
          placeholder="Buscar por @username o nombre..."
          value={q}
          onChange={e => setQ(e.target.value)}
          autoFocus
        />
        <div className="mkt-modal-list">
          {loading && <p className="mkt-modal-empty">Buscando...</p>}
          {!loading && q.trim().replace('@', '').length < 2 && (
            <p className="mkt-modal-empty" style={{ color: '#94a3b8' }}>Escribe al menos 2 caracteres para buscar</p>
          )}
          {!loading && q.trim().replace('@', '').length >= 2 && visible.length === 0 && (
            <div style={{ padding: '16px', textAlign: 'center' }}>
              <p className="mkt-modal-empty" style={{ marginBottom: 12 }}>
                Sin resultados para "{q}"
              </p>
              <button
                onClick={handleCreateNew}
                disabled={creating}
                style={{
                  padding: '8px 18px', background: '#71d234', color: '#002421',
                  border: 'none', borderRadius: 8, fontWeight: 700,
                  fontSize: 13, cursor: creating ? 'not-allowed' : 'pointer',
                  opacity: creating ? 0.7 : 1,
                }}
              >
                {creating ? 'Agregando...' : `+ Agregar @${q.trim().replace('@', '')}`}
              </button>
              <p style={{ fontSize: 11, color: '#4a5854', marginTop: 8 }}>
                Se creará el perfil y el scraper lo enriquecerá en la próxima ronda
              </p>
            </div>
          )}
          {visible.map(p => (
            <PickerRow
              key={p.id}
              profile={p}
              onSelect={handleSelect}
              selected={multi ? picked.has(p.id) : false}
            />
          ))}
        </div>
        {multi && (
          <div className="mkt-modal-footer">
            <span className="mkt-modal-count">{picked.size} seleccionado{picked.size !== 1 ? 's' : ''}</span>
            <button className="mkt-modal-confirm" onClick={handleConfirm} disabled={picked.size === 0}>
              Agregar seleccionados
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Sección MI MARCA ──────────────────────────────────────────────────
function MarcaSection({ profile, onSet, onRemove }) {
  const [showPicker, setShowPicker] = useState(false);
  const [expanded,  setExpanded]    = useState(false);
  const [stats,     setStats]       = useState(null);
  const [loadStats, setLoadStats]   = useState(false);
  const [errStats,  setErrStats]    = useState(null);
  const [tiktokUser, setTiktokUser] = useState(profile?.tiktok_username || '');
  const [savingTT,   setSavingTT]   = useState(false);
  const [editingTT,  setEditingTT]  = useState(false);

  const handleTikTokSave = async () => {
    setSavingTT(true);
    try {
      await contentService.setProfileTikTok(profile.id, tiktokUser);
      setEditingTT(false);
    } finally {
      setSavingTT(false);
    }
  };

  const handleExpand = async () => {
    const next = !expanded;
    setExpanded(next);
    if (next && !stats && profile) {
      setLoadStats(true);
      setErrStats(null);
      try {
        const r = await contentService.getMercadoProfileStats(profile.id);
        setStats(r.data);
      } catch {
        setErrStats('No se pudieron cargar las estadísticas.');
      } finally {
        setLoadStats(false);
      }
    }
  };

  // Reset stats cuando cambia el perfil
  const handleSet = (p) => { setStats(null); setExpanded(false); setTiktokUser(p?.tiktok_username || ''); onSet(p); };
  const handleRemove = () => { setStats(null); setExpanded(false); onRemove(); };

  const sortedDays = stats?.posting_days
    ? [...stats.posting_days].sort((a, b) => DAY_ORDER.indexOf(a.day) - DAY_ORDER.indexOf(b.day))
    : [];
  const maxDay    = sortedDays.length ? Math.max(...sortedDays.map(d => d.count)) : 1;
  const totalPosts = stats?.content_distribution?.reduce((s, c) => s + c.count, 0) || 0;

  return (
    <div className="mkt-section mkt-section--marca">
      <div className="mkt-section-header">
        <div>
          <h2 className="mkt-section-title">Mi Marca</h2>
          <p className="mkt-section-sub">El perfil de Instagram que representa tu marca</p>
        </div>
        {!profile && (
          <button className="mkt-add-btn mkt-add-btn--primary" onClick={() => setShowPicker(true)}>
            + Seleccionar marca
          </button>
        )}
      </div>

      {profile ? (
        <>
          <div className="mkt-marca-card">
            <ProfileAvatar profile={profile} size={64} />
            <div className="mkt-marca-info">
              <a
                href={`https://instagram.com/${profile.username}`}
                target="_blank" rel="noopener noreferrer"
                className="mkt-marca-username"
              >
                @{profile.username}
              </a>
              {profile.full_name && <span className="mkt-marca-name">{profile.full_name}</span>}
              <div className="mkt-marca-stats">
                <span><strong>{fmtFollowers(profile.followers_count)}</strong> seguidores</span>
                {profile.ai_category && <span className="mkt-marca-cat">{profile.ai_category}</span>}
              </div>
            </div>
            <div className="mkt-marca-actions">
              {editingTT ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600 }}>TT</span>
                  <input
                    autoFocus
                    value={tiktokUser}
                    onChange={e => setTiktokUser(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') handleTikTokSave(); if (e.key === 'Escape') setEditingTT(false); }}
                    placeholder="@handle"
                    disabled={savingTT}
                    style={{ fontSize: 11, padding: '3px 6px', width: 100, border: '1px solid #818cf8', borderRadius: 6, outline: 'none' }}
                  />
                  <button onClick={handleTikTokSave} disabled={savingTT}
                    style={{ fontSize: 11, padding: '3px 7px', borderRadius: 6, border: '1px solid #818cf8', background: '#818cf8', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>
                    {savingTT ? '…' : '✓'}
                  </button>
                  <button onClick={() => setEditingTT(false)}
                    style={{ fontSize: 11, padding: '3px 7px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer' }}>
                    ✕
                  </button>
                </div>
              ) : (
                <button onClick={() => setEditingTT(true)}
                  title={tiktokUser ? `TikTok: @${tiktokUser}` : 'Agregar TikTok'}
                  style={{
                    fontSize: 11, padding: '4px 10px', borderRadius: 6,
                    border: '1px solid #d1d5db',
                    background: tiktokUser ? '#f0f0fe' : '#fff',
                    color: tiktokUser ? '#4f46e5' : '#9ca3af',
                    cursor: 'pointer', fontWeight: tiktokUser ? 600 : 400,
                  }}
                >
                  {tiktokUser ? `TT @${tiktokUser}` : '+ TikTok'}
                </button>
              )}
              <button
                onClick={handleExpand}
                style={{
                  background: expanded ? '#f0fdf4' : '#fff',
                  border: '1px solid #d1d5db', borderRadius: 7,
                  padding: '4px 12px', fontSize: 12, fontWeight: 600,
                  cursor: 'pointer', color: expanded ? '#1d6c38' : '#374151',
                }}
              >
                {expanded ? '▲ Menos' : '▼ Analizar'}
              </button>
              <button className="mkt-change-btn" onClick={() => setShowPicker(true)}>Cambiar</button>
              <button className="mkt-remove-btn mkt-remove-btn--text" onClick={handleRemove}>Quitar</button>
            </div>
          </div>

          {/* Panel expandible — mismas secciones que ProfileCard sin "Análisis vs Mi Marca" */}
          {expanded && (
            <div style={{ marginTop: 14, borderTop: '1px solid #f3f4f6', paddingTop: 14 }}>
              {loadStats && <Loading fullScreen={false} mensaje="Cargando estadísticas" />}
              {errStats  && <p style={{ color: '#ef4444', fontSize: 13 }}>{errStats}</p>}

              {stats && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>

                  {/* Colaboradores */}
                  <div>
                    <p style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Colaboradores frecuentes</p>
                    {stats.collaborators.length === 0 ? (
                      <p style={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>Sin colaboraciones registradas</p>
                    ) : (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {stats.collaborators.map(c => (
                          <a key={c.username} href={`https://instagram.com/${c.username}`} target="_blank" rel="noopener noreferrer"
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#f3f4f6', borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 600, color: '#374151', textDecoration: 'none' }}>
                            @{c.username}<span style={{ color: '#9ca3af', fontWeight: 400 }}>×{c.count}</span>
                          </a>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Tipos de contenido */}
                  <div>
                    <p style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Tipo de contenido ({totalPosts} posts)</p>
                    {stats.content_distribution.length === 0 ? (
                      <p style={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>Sin datos</p>
                    ) : (
                      stats.content_distribution.map(c => (
                        <ContentBar key={c.media_type} label={MEDIA_LABELS[c.media_type] || c.media_type} count={c.count} total={totalPosts} color={MEDIA_COLORS[c.media_type] || '#6b7280'} />
                      ))
                    )}
                  </div>

                  {/* Días de publicación */}
                  <div style={{ gridColumn: '1 / -1' }}>
                    <p style={{ margin: '0 0 10px', fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Días de publicación más frecuentes</p>
                    {sortedDays.length === 0 ? (
                      <p style={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>Sin datos de fechas</p>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 60, marginTop: 28 }}>
                        {DAY_ORDER.map(dayName => {
                          const d   = sortedDays.find(x => x.day === dayName);
                          const cnt = d?.count || 0;
                          const h   = maxDay > 0 ? Math.round((cnt / maxDay) * 52) : 0;
                          const isTop = cnt === maxDay && cnt > 0;
                          return (
                            <div key={dayName} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                              <span style={{ fontSize: 10, color: isTop ? '#1d6c38' : '#9ca3af', fontWeight: isTop ? 700 : 400 }}>{cnt || ''}</span>
                              <div title={`${dayName}: ${cnt} posts`} style={{ width: '100%', height: h || 3, background: isTop ? '#1d6c38' : cnt > 0 ? '#bbf7d0' : '#f3f4f6', borderRadius: '3px 3px 0 0' }} />
                              <span style={{ fontSize: 10, color: '#6b7280' }}>{dayName}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* ¿Qué funciona mejor? */}
                  {(stats.collaborators.length > 0 || stats.content_performance?.length > 0) && (
                    <div style={{ gridColumn: '1 / -1', borderTop: '1px solid #f3f4f6', paddingTop: 14 }}>
                      <p style={{ margin: '0 0 10px', fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>¿Qué funciona mejor?</p>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                        {stats.collaborators.length > 0 && (
                          <div>
                            <p style={{ margin: '0 0 8px', fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase' }}>Top colaboradores (por engagement)</p>
                            {stats.collaborators.slice(0, 3).map((c, i) => {
                              const medals = ['1°', '2°', '3°'];
                              return (
                                <div key={c.username} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', background: i === 0 ? '#fefce8' : '#fafafa', borderRadius: 7, marginBottom: 5, border: i === 0 ? '1px solid #fde68a' : '1px solid #f3f4f6' }}>
                                  <span style={{ fontSize: 12, fontWeight: 700, color: '#374151' }}>{medals[i]} @{c.username}</span>
                                  <div style={{ textAlign: 'right', fontSize: 11, color: '#6b7280' }}>
                                    <div>❤️ {c.avg_likes.toLocaleString('es-CL')} · 💬 {c.avg_comments.toLocaleString('es-CL')}</div>
                                    <div style={{ color: '#9ca3af' }}>{c.count} collab{c.count !== 1 ? 's' : ''}</div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        {stats.content_performance?.length > 0 && (
                          <div>
                            <p style={{ margin: '0 0 8px', fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase' }}>Mejor tipo de contenido (por engagement)</p>
                            {stats.content_performance.slice(0, 3).map((cp, i) => {
                              const medals = ['1°', '2°', '3°'];
                              const label = MEDIA_LABELS[cp.media_type] || cp.media_type;
                              const color = MEDIA_COLORS[cp.media_type] || '#6b7280';
                              return (
                                <div key={cp.media_type} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', background: i === 0 ? '#f0fdf4' : '#fafafa', borderRadius: 7, marginBottom: 5, border: i === 0 ? `1px solid ${color}44` : '1px solid #f3f4f6' }}>
                                  <span style={{ fontSize: 12, fontWeight: 700, color }}>{medals[i]} {label}</span>
                                  <div style={{ textAlign: 'right', fontSize: 11, color: '#6b7280' }}>
                                    <div>❤️ {cp.avg_likes.toLocaleString('es-CL')} · 💬 {cp.avg_comments.toLocaleString('es-CL')}</div>
                                    <div style={{ color: '#9ca3af' }}>{cp.count} post{cp.count !== 1 ? 's' : ''}</div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Fórmula óptima */}
                  {stats.formula_ganadora?.length > 0 && (
                    <div style={{ gridColumn: '1 / -1', borderTop: '1px solid #f3f4f6', paddingTop: 14 }}>
                      <p style={{ margin: '0 0 12px', fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Fórmula óptima de publicación</p>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 16 }}>
                        {stats.formula_ganadora.map((f, i) => {
                          const medals  = ['1°', '2°', '3°'];
                          const bgs     = ['#fefce8', '#f8fafc', '#fafaf9'];
                          const borders = ['#fde68a', '#e2e8f0', '#e7e5e4'];
                          const label   = MEDIA_LABELS[f.media_type] || f.media_type;
                          const color   = MEDIA_COLORS[f.media_type] || '#6b7280';
                          return (
                            <div key={i} style={{ background: bgs[i], border: `1px solid ${borders[i]}`, borderRadius: 10, padding: '10px 12px' }}>
                              <div style={{ fontSize: 13, fontWeight: 700, color: '#9ca3af', marginBottom: 4 }}>{medals[i]}</div>
                              <div style={{ fontSize: 13, fontWeight: 700, color, marginBottom: 6 }}>{label}</div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                <span style={{ fontSize: 11, background: '#f3f4f6', borderRadius: 12, padding: '2px 8px', display: 'inline-block', color: '#374151', fontWeight: 600 }}>📅 {f.day}</span>
                                <span style={{ fontSize: 11, background: f.is_collab ? '#ecfdf5' : '#f3f4f6', borderRadius: 12, padding: '2px 8px', display: 'inline-block', color: f.is_collab ? '#059669' : '#6b7280', fontWeight: 600 }}>
                                  {f.is_collab ? 'Con collab' : 'Solo'}
                                </span>
                              </div>
                              <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #f3f4f6' }}>
                                <div style={{ fontSize: 12, fontWeight: 800, color: '#111827' }}>{f.avg_engagement.toLocaleString('es-CL')} eng.</div>
                                <div style={{ fontSize: 10, color: '#9ca3af' }}>❤️ {f.avg_likes.toLocaleString('es-CL')} · 💬 {f.avg_comments.toLocaleString('es-CL')} · {f.n} posts</div>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Tabla por tipo */}
                      {stats.por_tipo && Object.keys(stats.por_tipo).length > 0 && (
                        <div style={{ marginBottom: 16 }}>
                          <p style={{ margin: '0 0 8px', fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase' }}>Mejor estrategia por tipo</p>
                          <div style={{ borderRadius: 8, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                              <thead>
                                <tr style={{ background: '#f9fafb' }}>
                                  <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 700, color: '#374151' }}>Tipo</th>
                                  <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 700, color: '#374151' }}>Mejor día</th>
                                  <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 700, color: '#374151' }}>¿Con collab?</th>
                                  <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 700, color: '#374151' }}>Impacto collab</th>
                                  <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700, color: '#374151' }}>Mejor eng.</th>
                                </tr>
                              </thead>
                              <tbody>
                                {Object.entries(stats.por_tipo).map(([mt, d], idx) => {
                                  const label = MEDIA_LABELS[mt] || mt;
                                  const color = MEDIA_COLORS[mt] || '#6b7280';
                                  const boost = d.collab_boost_pct;
                                  const boostColor = boost == null ? '#9ca3af' : boost > 0 ? '#10b981' : '#ef4444';
                                  const boostText  = boost == null ? '—' : boost > 0 ? `+${boost}%` : `${boost}%`;
                                  return (
                                    <tr key={mt} style={{ borderTop: idx > 0 ? '1px solid #f3f4f6' : 'none', background: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                                      <td style={{ padding: '8px 12px' }}><span style={{ fontWeight: 700, color }}>{label}</span></td>
                                      <td style={{ padding: '8px 10px', textAlign: 'center' }}><span style={{ background: '#eff6ff', color: '#2563eb', borderRadius: 12, padding: '2px 8px', fontWeight: 600 }}>{d.mejor_dia || '—'}</span></td>
                                      <td style={{ padding: '8px 10px', textAlign: 'center' }}>{d.collab_boost_pct == null ? '—' : d.collab_boost_pct > 0 ? <span style={{ color: '#059669', fontWeight: 700 }}>Sí</span> : <span style={{ color: '#6b7280', fontWeight: 600 }}>No</span>}</td>
                                      <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                                        <span style={{ fontWeight: 700, color: boostColor }}>{boostText}</span>
                                        {boost != null && <div style={{ fontSize: 10, color: '#9ca3af' }}>{d.n_con_collab} vs {d.n_sin_collab} posts</div>}
                                      </td>
                                      <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700, color: '#111827' }}>{d.mejor_combo_engagement?.toLocaleString('es-CL') ?? '—'}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Collab vs Solo */}
                      {stats.collab_impact?.length > 0 && (
                        <div>
                          <p style={{ margin: '0 0 8px', fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase' }}>Collab vs Solo — comparación de engagement</p>
                          {stats.collab_impact.filter(c => c.con_collab != null && c.sin_collab != null).map(c => {
                            const label  = MEDIA_LABELS[c.media_type] || c.media_type;
                            const color  = MEDIA_COLORS[c.media_type] || '#6b7280';
                            const maxVal = Math.max(c.con_collab || 0, c.sin_collab || 0) || 1;
                            const pctCon = Math.round((c.con_collab / maxVal) * 100);
                            const pctSin = Math.round((c.sin_collab / maxVal) * 100);
                            const boost  = c.boost_pct;
                            const boostColor = boost > 0 ? '#10b981' : '#ef4444';
                            return (
                              <div key={c.media_type} style={{ marginBottom: 10 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                  <span style={{ fontSize: 12, fontWeight: 700, color }}>{label}</span>
                                  {boost != null && <span style={{ fontSize: 11, fontWeight: 700, color: boostColor }}>{boost > 0 ? `Collab suma ${boost}%` : `Collab resta ${Math.abs(boost)}%`}</span>}
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                  <div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#6b7280', marginBottom: 2 }}>
                                      <span>Con collab ({c.n_collab} posts)</span>
                                      <span style={{ fontWeight: 600 }}>{(c.con_collab || 0).toLocaleString('es-CL')}</span>
                                    </div>
                                    <div style={{ height: 8, background: '#f3f4f6', borderRadius: 4, overflow: 'hidden' }}>
                                      <div style={{ height: '100%', width: `${pctCon}%`, background: '#059669', borderRadius: 4 }} />
                                    </div>
                                  </div>
                                  <div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#6b7280', marginBottom: 2 }}>
                                      <span>Sin collab ({c.n_solo} posts)</span>
                                      <span style={{ fontWeight: 600 }}>{(c.sin_collab || 0).toLocaleString('es-CL')}</span>
                                    </div>
                                    <div style={{ height: 8, background: '#f3f4f6', borderRadius: 4, overflow: 'hidden' }}>
                                      <div style={{ height: '100%', width: `${pctSin}%`, background: '#94a3b8', borderRadius: 4 }} />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}

                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <div className="mkt-empty">
          <span>Selecciona el perfil de Instagram que representa tu marca.</span>
        </div>
      )}

      {showPicker && (
        <ProfilePickerModal
          multi={false}
          onSelect={([p]) => { handleSet(p); setShowPicker(false); }}
          onClose={() => setShowPicker(false)}
        />
      )}
    </div>
  );
}

// ── Sección genérica (Competencia / Referencia) ───────────────────────
function MercadoSection({ title, subtitle, role, accentClass, profiles, onAdd, onRemove, marcaId }) {
  const [showPicker, setShowPicker] = useState(false);
  const existingIds = profiles.map(p => p.id);

  return (
    <div className={`mkt-section ${accentClass}`}>
      <div className="mkt-section-header">
        <div>
          <h2 className="mkt-section-title">{title}</h2>
          <p className="mkt-section-sub">{subtitle}</p>
        </div>
        <button className="mkt-add-btn" onClick={() => setShowPicker(true)}>+ Agregar perfil</button>
      </div>

      {profiles.length === 0 ? (
        <div className="mkt-empty">
          <span>Sin perfiles. Haz clic en "Agregar perfil" para seleccionar desde /tracked/.</span>
        </div>
      ) : (
        <div className="mkt-profiles-grid">
          {profiles.map(p => (
            <ProfileCard key={p.id} profile={p} role={role} onRemove={() => onRemove(p.id)} marcaId={marcaId} />
          ))}
        </div>
      )}

      {showPicker && (
        <ProfilePickerModal
          multi
          selectedIds={existingIds}
          onSelect={(selected) => { selected.forEach(p => { if (!existingIds.includes(p.id)) onAdd(p); }); }}
          onClose={() => setShowPicker(false)}
        />
      )}
    </div>
  );
}

// ── Página principal ──────────────────────────────────────────────────
export default function MercadoPage() {
  const [marca,       setMarca]       = useState(null);
  const [competencia, setCompetencia] = useState([]);
  const [referencia,  setReferencia]  = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [saving,      setSaving]      = useState(false);

  useEffect(() => {
    contentService.getMercado()
      .then(r => {
        setMarca(r.data.marca || null);
        setCompetencia(r.data.competencia || []);
        setReferencia(r.data.referencia || []);
      })
      .finally(() => setLoading(false));
  }, []);

  const handleSetMarca = async (profile) => {
    setSaving(true);
    await contentService.setMercadoMarca(profile.id);
    setMarca(profile);
    setSaving(false);
  };

  const handleRemoveMarca = async () => {
    setSaving(true);
    await contentService.removeMercadoMarca();
    setMarca(null);
    setSaving(false);
  };

  const handleAdd = async (role, profile) => {
    setSaving(true);
    await contentService.addMercadoRole(role, profile.id);
    if (role === 'competencia') setCompetencia(prev => [...prev, profile]);
    else setReferencia(prev => [...prev, profile]);
    setSaving(false);
  };

  const handleRemove = async (role, profileId) => {
    setSaving(true);
    await contentService.removeMercadoRole(role, profileId);
    if (role === 'competencia') setCompetencia(prev => prev.filter(p => p.id !== profileId));
    else setReferencia(prev => prev.filter(p => p.id !== profileId));
    setSaving(false);
  };

  if (loading) return <Loading />;

  return (
    <div className="mkt-page">
      <div className="mkt-page-header">
        <div className="pig-topbar-left">
          <h1 className="pig-topbar-title">
            <i className="bi bi-bar-chart-line" /> Mercado
          </h1>
          <p className="pig-topbar-sub">
            Define tu marca, la competencia y las referencias para analizar el mercado.
          </p>
        </div>
        {saving && <span className="mkt-saving">Guardando...</span>}
      </div>

      <MarcaSection profile={marca} onSet={handleSetMarca} onRemove={handleRemoveMarca} />

      <MercadoSection
        title="Competencia"
        subtitle="Perfiles que compiten directamente con tu marca"
        role="competencia"
        accentClass="mkt-section--competencia"
        profiles={competencia}
        onAdd={(p) => handleAdd('competencia', p)}
        onRemove={(id) => handleRemove('competencia', id)}
        marcaId={marca?.id || null}
      />

      <MercadoSection
        title="Referencia"
        subtitle="Perfiles de inspiración o benchmarks para tu marca"
        role="referencia"
        accentClass="mkt-section--referencia"
        profiles={referencia}
        onAdd={(p) => handleAdd('referencia', p)}
        onRemove={(id) => handleRemove('referencia', id)}
        marcaId={marca?.id || null}
      />
    </div>
  );
}
